'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { useFplCurrentGameweek, useFplStats, type FplGameweekSelection } from '@/lib/api/fpl-routes'
import type { FplPosition, FplStatMetric } from '@/lib/types/fpl'
import { Spinner } from '@/components/ui/spinner'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const METRICS: { value: FplStatMetric; label: string }[] = [
  { value: 'points', label: 'Points' },
  { value: 'bonus_points', label: 'Bonus Points' },
  { value: 'defcon', label: 'DEFCON' },
  { value: 'xga', label: 'xGA' },
  { value: 'ga', label: 'G/A' },
  { value: 'shots', label: 'Shots' },
  { value: 'shot_accuracy', label: 'Shot Accuracy' },
  { value: 'shot_conversion', label: 'Shot Conversion' },
  { value: 'errors_to_shot', label: 'Errors Leading to Shot' },
  { value: 'dispossessed', label: 'Dispossessed' },
  { value: 'big_chances_created', label: 'Big Chances Created' },
  { value: 'big_chances_missed', label: 'Big Chances Missed' },
  { value: 'succ_dribbles', label: 'Successful Dribbles' },
  { value: 'dribbled_past', label: 'Dribbled Past' },
  { value: 'touches', label: 'Touches' },
  { value: 'passes_completed', label: 'Passes Completed' },
  { value: 'passes_into_final_third', label: 'Passes into Final 3rd' },
  { value: 'accurate_crosses', label: 'Accurate Crosses' },
  { value: 'accurate_long_passes', label: 'Accurate Long Passes' },
  { value: 'line_breaking_passes', label: 'Line Breaking Passes' },
  { value: 'fouled', label: 'Most Fouled' },
  { value: 'sprints', label: 'Sprints' },
  { value: 'sprinting_distance', label: 'Sprinting Distance' },
  { value: 'distance_covered', label: 'Distance Covered' },
  { value: 'running_distance', label: 'Running Distance' },
  { value: 'transferred_in', label: 'Transferred In' },
  { value: 'transferred_out', label: 'Transferred Out' },
]

const PERCENTAGE_METRICS: FplStatMetric[] = ['shot_accuracy', 'shot_conversion']
const DISTANCE_METRICS: FplStatMetric[] = ['sprinting_distance', 'running_distance', 'distance_covered']

const TRANSFER_METRICS: FplStatMetric[] = ['transferred_in', 'transferred_out']

const POSITIONS: { value: FplPosition | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'GKP', label: 'GK' },
  { value: 'DEF', label: 'DEF' },
  { value: 'MID', label: 'MID' },
  { value: 'FWD', label: 'FWD' },
]

function formatValue(metric: FplStatMetric, value: number | null): string {
  if (value == null) return '—'
  if (PERCENTAGE_METRICS.includes(metric)) return `${value.toFixed(1)}%`
  if (DISTANCE_METRICS.includes(metric)) return `${value.toLocaleString()}m`
  return value.toLocaleString()
}

export function BasicFPLStats() {
  const { data: currentGwData } = useFplCurrentGameweek()
  const currentGameweek = currentGwData?.data.gameweek_number ?? null

  const [gameweek, setGameweek] = useState<FplGameweekSelection | null>(null)
  const [metric, setMetric] = useState<FplStatMetric>('points')
  const [position, setPosition] = useState<FplPosition | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    if (currentGameweek != null && gameweek == null) setGameweek(currentGameweek)
  }, [currentGameweek, gameweek])

  // Transfers are per-gameweek deltas, not a running total — "Season"
  // doesn't mean anything for them, so bump back to the current gameweek
  // if the user picks a transfer metric while on the season view.
  const isTransferMetric = TRANSFER_METRICS.includes(metric)
  useEffect(() => {
    if (isTransferMetric && gameweek === 'season' && currentGameweek != null) {
      setGameweek(currentGameweek)
    }
  }, [isTransferMetric, gameweek, currentGameweek])

  const { data, isLoading, isError } = useFplStats(gameweek, metric, position, 12)
  const entries = data?.data ?? []
  const maxValue = Math.max(1, ...entries.map((e) => Math.abs(e.value ?? 0)))

  const atMin = gameweek != null && gameweek !== 'season' && gameweek <= 1
  const atMax = gameweek === 'season' || (isTransferMetric && gameweek === currentGameweek)
  const metricLabel = METRICS.find((m) => m.value === metric)?.label ?? 'Most Transferred'

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="flex items-center gap-1.5 text-lg font-semibold text-white">
              {metricLabel}
              <ChevronDown className="size-4 text-white/50" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1">
            <div className="flex max-h-72 flex-col overflow-y-auto">
              {METRICS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => {
                    setMetric(m.value)
                    setMenuOpen(false)
                  }}
                  className={cn(
                    'rounded-sm px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                    metric === m.value ? 'text-white font-medium' : 'text-foreground'
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="flex items-center rounded-md border border-white/10 p-0.5">
          {POSITIONS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setPosition(p.value)}
              className={cn(
                'rounded px-2 py-1 text-xs font-medium transition-colors',
                position === p.value ? 'bg-white text-black' : 'text-white/60 hover:text-white'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          aria-label="Previous gameweek"
          disabled={gameweek == null || atMin}
          onClick={() =>
            setGameweek((gw) => {
              if (gw == null) return gw
              if (gw === 'season') return currentGameweek ?? gw
              return Math.max(1, gw - 1)
            })
          }
          className="flex size-7 items-center justify-center rounded-md border border-white/10 text-white/60 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="min-w-16 text-center text-sm text-white/70">
          {gameweek == null ? '—' : gameweek === 'season' ? 'Season' : `GW ${gameweek}`}
        </span>
        <button
          type="button"
          aria-label="Next gameweek"
          disabled={gameweek == null || atMax}
          onClick={() =>
            setGameweek((gw) => {
              if (gw == null || gw === 'season' || currentGameweek == null) return gw
              if (gw >= currentGameweek) return isTransferMetric ? gw : 'season'
              return gw + 1
            })
          }
          className="flex size-7 items-center justify-center rounded-md border border-white/10 text-white/60 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="mt-3">
        {isLoading && (
          <div className="flex justify-center py-6">
            <Spinner className="size-5 text-white/50" />
          </div>
        )}

        {isError && <p className="text-sm text-red-400">Failed to load stats.</p>}

        {!isLoading && !isError && entries.length === 0 && (
          <p className="text-sm text-white/40">No data for this gameweek.</p>
        )}

        <div key={`${gameweek}-${metric}-${position}`}>
          {entries.map((entry, i) => {
            // Cap the fill at 60% (not 100%) so the longest bar still leaves
            // room for the value label after it, on mobile, instead of
            // running the fill all the way to where the number sits.
            const barWidth = Math.max(4, (Math.abs(entry.value ?? 0) / maxValue) * 60)
            return (
              <div
                key={entry.player_id ?? entry.web_name}
                style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'backwards' }}
                className="flex animate-in items-center gap-2 border-b border-white/10 px-2 py-2 fade-in-0 slide-in-from-left-1 duration-300 ease-out last:border-b-0 sm:gap-3"
              >
                <span className="w-4 shrink-0 text-right text-xs font-medium text-white/40">{i + 1}</span>
                <span className="flex size-4 shrink-0 items-center justify-center">
                  {entry.team_logo_url && <img src={entry.team_logo_url} alt="" className="size-4 object-contain" />}
                </span>
                {entry.fpl_id != null ? (
                  <Link
                    href={`/fpl/players/${entry.fpl_id}`}
                    className="fpl-row-link w-28 shrink-0 truncate text-sm sm:w-32"
                  >
                    {entry.web_name}
                  </Link>
                ) : (
                  <span className="w-28 shrink-0 truncate text-sm text-white sm:w-32">{entry.web_name}</span>
                )}
                <span className="relative flex min-w-0 flex-1 items-center sm:gap-1.5">
                  <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10 sm:h-1.5">
                    <span
                      className="block h-full rounded-full transition-[width] duration-500 ease-out"
                      style={{ width: `${barWidth}%`, backgroundColor: entry.team_color_hex || '#dc2626' }}
                    />
                  </span>
                  {/* Mobile: pin the value right after the bar's own fill (via a CSS var so
                      it tracks barWidth) instead of the desktop's fixed far-right column —
                      on a narrow track a value pinned at the row's edge reads as detached
                      from a short bar. sm:static/sm:left-auto restores the original layout. */}
                  <span
                    className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-sm font-semibold text-white sm:static sm:top-auto sm:shrink-0 sm:translate-y-0 sm:pl-0 sm:text-right sm:text-base"
                    style={{ left: `${barWidth}%` }}
                  >
                    {formatValue(metric, entry.value)}
                    {entry.secondary_value && (
                      <span className="ml-1 text-xs font-normal text-white/50">({entry.secondary_value})</span>
                    )}
                  </span>
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
